import { memo, useState, useCallback } from "react";
import {
  Dialog,
  Stack,
  IconButton,
  Box,
  Typography,
  Button,
  CircularProgress,
  DialogTitle,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ExtractedAnalyte } from "../../../hooks/types";
import { AnalytesList } from "./AnalytesList";

interface ExtractionDialogProps {
  open: boolean;
  onClose: () => void;
  fileBase64: string | null;
  isExtracting: boolean;
  extractionError: string | null;
  extractedAnalytes: ExtractedAnalyte[];
  onRetryExtraction: () => void;
  onSelectAnalyte: (index: number) => void;
  onSelectAll: (checked: boolean) => void;
  onAddSelectedAnalytes: () => void;
  handleSave: () => void;
  onStartExtraction: () => void;
  examTitle?: string;
  viewOnly?: boolean;
}

export const ExtractionDialog = memo(
  ({
    open,
    onClose,
    fileBase64,
    isExtracting,
    extractionError,
    extractedAnalytes,
    onRetryExtraction,
    onSelectAnalyte,
    onSelectAll,
    onAddSelectedAnalytes,
    onStartExtraction,
    examTitle,
    viewOnly,
    handleSave,
  }: ExtractionDialogProps) => {
    const hasSelectedAnalytes = extractedAnalytes.some((a) => a.selected);
    const [hasStartedExtraction, setHasStartedExtraction] = useState(false);

    // Função para lidar com o fechamento do dialog
    const handleCloseDialog = useCallback(() => {
      // Limpa qualquer seleção antes de fechar
      onSelectAll(false);
      setHasStartedExtraction(false);
      onClose();
    }, [onClose, onSelectAll]);

    const handleStartExtraction = () => {
      setHasStartedExtraction(true);
      onStartExtraction();
    };

    const renderContent = () => {
      // Se ainda não iniciou a extração, mostra o botão de iniciar
      if (!hasStartedExtraction && !extractionError) {
        return (
          <Stack spacing={3} alignItems="center" sx={{ p: 2 }}>
            <InfoOutlinedIcon color="info" sx={{ fontSize: 48 }} />
            <Typography variant="h6" textAlign="center">
              Documento Carregado
            </Typography>
            <Typography color="text.secondary" textAlign="center">
              Clique no botão abaixo para iniciar a extração automática dos
              analitos{" "}
              {!viewOnly && "ou salve o documento sem realizar a extração"}.
            </Typography>
            <Stack direction="row" spacing={2}>
              {!viewOnly ? (
                <Button variant="outlined" onClick={handleSave}>
                  Salvar sem extração
                </Button>
              ) : (
                <Button variant="outlined" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
              )}

              <Button variant="contained" onClick={handleStartExtraction}>
                Iniciar Extração
              </Button>
            </Stack>
          </Stack>
        );
      }

      if (isExtracting) {
        return (
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <CircularProgress />
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
            >
              Extraindo analitos...
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Isso pode levar alguns segundos
            </Typography>
          </Stack>
        );
      }

      if (extractionError) {
        return (
          <Stack spacing={3} alignItems="center" sx={{ p: 2 }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
            <Typography variant="h6" color="error" textAlign="center">
              Erro na Extração
            </Typography>
            <Typography color="text.secondary" textAlign="center">
              {extractionError}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleCloseDialog}>
                Fechar
              </Button>
              <Button variant="contained" onClick={onRetryExtraction}>
                Tentar Novamente
              </Button>
            </Stack>
          </Stack>
        );
      }

      if (extractedAnalytes.length === 0) {
        return (
          <Stack spacing={3} alignItems="center" sx={{ p: 2 }}>
            <InfoOutlinedIcon color="info" sx={{ fontSize: 48 }} />
            <Typography variant="h6" textAlign="center">
              Nenhum Analito Encontrado
            </Typography>
            <Typography color="text.secondary" textAlign="center">
              Não foram encontrados analitos neste documento. Verifique se o
              arquivo é um resultado de exame válido ou insira os dados
              manualmente.
            </Typography>
            <Button variant="outlined" onClick={handleCloseDialog}>
              Fechar
            </Button>
          </Stack>
        );
      }

      return (
        <Stack spacing={2} sx={{ height: "100%" }}>
          <AnalytesList
            analytes={extractedAnalytes}
            onSelectAnalyte={onSelectAnalyte}
            onSelectAll={onSelectAll}
          />

          <Stack direction="row" spacing={2} sx={{ mt: "auto" }}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={handleCloseDialog}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={onAddSelectedAnalytes}
              disabled={!hasSelectedAnalytes}
            >
              Adicionar Selecionados
            </Button>
          </Stack>
        </Stack>
      );
    };

    return (
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          {examTitle || "Extração de Analitos"}
        </DialogTitle>
        <Stack direction="row" spacing={2} sx={{ height: "80vh" }}>
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: "absolute", right: 8, top: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>

          {/* PDF Preview */}
          <Box sx={{ flex: 1, height: "100%", p: 2 }}>
            <iframe
              src={fileBase64 || ""}
              style={{
                width: "100%",
                height: "100%",
                border: "1px solid #ccc",
                borderRadius: 4,
              }}
              title="PDF Preview"
            />
          </Box>

          {/* Analitos Extraídos */}
          <Box
            sx={{
              width: "450px",
              height: "100%",
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {renderContent()}
          </Box>
        </Stack>
      </Dialog>
    );
  }
);
